import { useEffect, useRef, useState, type ReactNode } from 'react'

interface DeferRenderProps {
  children: ReactNode
  /**
   * Reserve vertical space while the content is unmounted so the page
   * doesn't jump (CLS) when it mounts. Roughly the section's height.
   */
  minHeight?: number
  /**
   * Start mounting when the placeholder is within this margin of the
   * viewport. Default 600px so content is ready just before the user
   * scrolls to it.
   */
  rootMargin?: string
}

/**
 * Defer rendering of a below-the-fold section until the user scrolls
 * near it (IntersectionObserver) or the browser goes idle as a
 * fallback. This keeps the hero (LCP) from competing for main-thread
 * time, network, and layout with heavy sections (image grids, data
 * fetches, canvas, framer-motion) that aren't visible yet.
 *
 * A reserved-height placeholder prevents layout shift.
 */
export function DeferRender({
  children,
  minHeight = 400,
  rootMargin = '600px',
}: DeferRenderProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (show) return
    const el = ref.current
    if (!el) return

    // Primary trigger: the section nears the viewport.
    let io: IntersectionObserver | null = null
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setShow(true)
            io?.disconnect()
          }
        },
        { rootMargin },
      )
      io.observe(el)
    } else {
      setShow(true)
    }

    // Safety net: mount during idle time even if the user never
    // scrolls, so SEO crawlers and fast scrollers still get content.
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number
      }
    ).requestIdleCallback
    const idleId = typeof ric === 'function'
      ? ric(() => setShow(true), { timeout: 4000 })
      : window.setTimeout(() => setShow(true), 2500)

    return () => {
      io?.disconnect()
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback
      if (typeof cic === 'function') cic(idleId)
      else window.clearTimeout(idleId)
    }
  }, [show, rootMargin])

  if (show) return <div className="page-enter">{children}</div>
  return <div ref={ref} aria-hidden style={{ minHeight }} />
}
